import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRoomTopics } from '../api/rooms';
import { UserContext } from '../functions/Auth';

// component
import TabPanel from '../components/TabPanel';
import TopicList from '../components/TopicList';
import TopicModal from '../components/TopicModal';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';

// types
type TopicListType = {
    body: string,
    commentCount: number,
    openRange: number, // 公開範囲(招待:0 / 公開:1)
    postStatus: number, // 投稿ステータス(下書き:0 / 投稿:1)
    postTime: string, // 投稿時間 (yyyy-MM-dd HH:mm:ss)
    title: string,
    topicId: number,
    topicMembers: null | {
        email: string,
        userId: number,
        userName: string,
    }[],
    updatedTime: string, // 更新時間 (yyyy-MM-dd HH:mm:ss)
    userId: number,
    userName: string,
}
type OpenTopicListType = Omit<TopicListType, "openRange"> & { openRange: 1 };
type ClosedTopicListType = Omit<TopicListType, "openRange"> & { openRange: 0 };

export default function Room() {
    const params = useParams();
    const roomId = params.roomId as string;
    const userInfo = useContext(UserContext);
    const userId = userInfo?.userId as number;
    const roomInfo = userInfo?.rooms.find(room => room.roomId.toString() === roomId);

    // トピックの取得
    const [topicList, setTopicList] = useState<TopicListType[]>([]); // トピックリスト
    const [openTopicList, setOpenTopicList] = useState<OpenTopicListType[]>([]); // 公開トピックリスト
    const [closedTopicList, setClosedTopicList] = useState<ClosedTopicListType[]>([]); // 限定トピックリスト

    const fnGetRoomTopics = async (roomId: string, userId: number) => {
        const response = await getRoomTopics(roomId, userId);
        if (response) {
            setTopicList(response.reverse());
        }
    };

    const fnSeparateByOpenRange = (topicList:TopicListType[]) => {
        const openTopicList: OpenTopicListType[] = [];
        const closedTopicList: ClosedTopicListType[] = [];

        topicList.forEach((topic) => {
            if (topic.openRange === 1) {
                openTopicList.push(topic as OpenTopicListType);
            } else if (topic.openRange === 0) {
                closedTopicList.push(topic as ClosedTopicListType);
            }
        });

        setOpenTopicList(openTopicList);
        setClosedTopicList(closedTopicList);
    }

    // トピックリストの作成
    useEffect(() => {
        fnGetRoomTopics(roomId, userId);
    }, [roomId, userId]);

    // 公開、限定トピックリストの作成
    useEffect(() => {
        fnSeparateByOpenRange(topicList);
    }, [topicList]);

    // タブ
    const [value, setValue] = React.useState(0);
    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    function a11yProps(index: number) {
        return {
            id: `tab-${index}`,
            'aria-controls': `tabpanel-${index}`,
        };
    }

    /**
     * モーダル
     */
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState(false);

    // 更新対象のトピックID
    const [targetEditTopic, setTargetEditTopic] = useState(0);

    return (
        <Box>
            {/* タブ */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={value} onChange={handleChangeTab} aria-label="tabs" centered={true}>
                    <Tab label="全て" {...a11yProps(0)} />
                    <Tab label="公開" {...a11yProps(1)} />
                    <Tab label="限定" {...a11yProps(2)} />
                </Tabs>
            </Box>

            {/* ページ情報 */}
            <Box
                sx={{
                    maxWidth: 980,
                    mx: "auto",
                    pt: 3,
                    px: 3,
                }}
            >
                <Typography
                    variant="h5"
                    component="h1"
                    fontWeight="700"
                    gutterBottom
                >{roomInfo?.name}</Typography>
                <Typography
                    paragraph
                >{roomInfo?.description}</Typography>
            </Box>

            {/* タブ - コンテンツ */}
            <TabPanel value={value} index={0}>
                {/* 全てのトピック */}
                <TopicList
                    isShowCardActions={false}
                    topicList={topicList}
                    setIsEdit={setIsEdit}
                    setIsOpenModal={setIsOpenModal}
                    setTargetEditTopic={setTargetEditTopic}
                />
            </TabPanel>
            <TabPanel value={value} index={1}>
                {/* 公開トピック */}
                <TopicList
                    isShowCardActions={false}
                    topicList={openTopicList}
                    setIsEdit={setIsEdit}
                    setIsOpenModal={setIsOpenModal}
                    setTargetEditTopic={setTargetEditTopic}
                />
            </TabPanel>
            <TabPanel value={value} index={2}>
                {/* 限定トピック */}
                <TopicList
                    isShowCardActions={false}
                    topicList={closedTopicList}
                    setIsEdit={setIsEdit}
                    setIsOpenModal={setIsOpenModal}
                    setTargetEditTopic={setTargetEditTopic}
                />
            </TabPanel>

            {/* トピック作成ボタン */}
            <Button
                onClick={() => {
                    setIsOpenModal(true);
                }}
                sx={{
                    position: "fixed",
                    right: 16,
                    bottom: 16,
                    zIndex: 100,
                }}
                variant="contained">新規トピック作成
            </Button>

            {/* トピック作成モーダル */}
            <TopicModal
                fnGetRoomTopics={fnGetRoomTopics}
                isEdit={isEdit}
                isOpen={isOpenModal}
                setIsEdit={setIsEdit}
                setIsOpen={setIsOpenModal}
                targetEditTopic={targetEditTopic}
                topicType={"new"}
            />
        </Box>
    );
}